<script setup>
import { ref, nextTick } from 'vue'
import { useUsersStore, useReservationsStore } from "@/stores"
import { storeToRefs } from 'pinia'
import MembersInfoDialog from '@/@core/components/MembersInfoDialog.vue'
import RestaurantsInfoDialog from '@/@core/components/RestaurantsInfoDialog.vue'
import MembersInfoContent from '@/@core/components/MembersInfoContent.vue'
import PreferredTabsContent from '@/@core/components/PreferredTabsContent.vue'
import CmsText from '@/@core/components/CmsText.vue'

//define props joinPage
const props = defineProps({
  homePage: {
    type: Boolean,
    default: false
  },
  joinPage: {
    type: Boolean,
    default: false
  },
  verifiedElement: {
    type: Boolean,
    default: false
  },
  bookBtn: {
    type: Boolean,
    default: true
  },
  learnMoreFirst: {
    type: Boolean,
    default: false
  },
  activateText: {
    type: String,
    default: "Activate"
  },
  freePlatDur: {
    type: String,
    default: ""
  },
  forClients: {
    type: String,
    default: ""
  },
  iosAppAvailable: {
    type: Boolean,
    default: false
  },
  showActivateSubText: {
    type: Boolean,
    default: false
  },
  showRestaurantsLink: {
    type: Boolean,
    default: true
  },
  customHeadline: {
    type: String,
    default: ""
  }
})
const isMembersDialogOpen = ref(false)
const isRestaurantsDialogOpen = ref(false)

// Members / Restaurants expand in place (no modal, no navigation)
const expandedSection = ref(null)
const expandedPanelEl = ref(null)

const toggleSection = async section => {
  expandedSection.value = expandedSection.value === section ? null : section
  if (expandedSection.value) {
    await nextTick()
    expandedPanelEl.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })

    // VTabs mis-measures when mounted mid-expand (FAQs tab laid out at zero);
    // nudge Vuetify's slide group to re-measure once the transition settles
    setTimeout(() => window.dispatchEvent(new Event('resize')), 350)
  }
}

const usersStore = useUsersStore()
let isRunningInIOSAppStore = null
let loginDialogMode = null
let loginDialogVerificationType = null
let myUser = null
let isShowingPreferredRequestInfoDialog = null;
({ myUser, isRunningInIOSAppStore, loginDialogMode, loginDialogVerificationType, isShowingPreferredRequestInfoDialog } = storeToRefs(usersStore))

const reservationsStore = useReservationsStore()

const openSearch = () => {
  usersStore.activateMainSearch()
}

const premiumDurations = {
  "individual-1cycle-1month": {
    duration: "1 month",
    code: "individual-1cycle-1month",
  },
  "individual-1cycle-2month": {
    duration: "2 months",
    code: "individual-1cycle-2month",
  },
  "individual-1cycle-3month": {
    duration: "3 months",
    code: "individual-1cycle-3month",
  },
  "individual-1cycle-annual": {
    duration: "1 year",
    code: "individual-1cycle-annual",
  },
  "individual-1cycle-1month-1free": {
    duration: "1 month",
    code: "individual-1cycle-2month",
  },
  "individual-1cycle-2month-1free": {
    duration: "2 months",
    code: "individual-1cycle-2month",
  },
  "individual-1cycle-3month-1free": {
    duration: "3 months",
    code: "individual-1cycle-3month",
  },
  "individual-1cycle-annual-1free": {
    duration: "1 year",
    code: "individual-1cycle-annual-1free",
  },
  "preferred-trial-fixed-end-2024-01-31": {
    duration: "1 month",
    code: "preferred-trial-fixed-end-2024-01-31",
  },
  "preferred-trial-fixed-end-2024-01-31": {
    duration: "1 month",
    code: "preferred-trial-fixed-end-2024-01-31",
  }
}

const premiumDurationSettings = { available: false }

if (props.freePlatDur != "") {
  premiumDurationSettings.available = true
  premiumDurationSettings.duration = premiumDurations[props.freePlatDur].duration
  premiumDurationSettings.code = premiumDurations[props.freePlatDur].code
}

/*
 * Referral/activation pages: ONE personalized line under the network line,
 * driven by admin-editable group fields (never hardcoded per page).
 * /join and /activate pass the group's Join Message verbatim as for-clients;
 * /friends and /spouse compose {benefit} + {for whom}, e.g.
 * "1 month free for Kapil Chaudhary's friends, if approved".
 */
const benefitText = props.customHeadline !== ''
  ? props.customHeadline
  : premiumDurationSettings.available
    ? `${premiumDurationSettings.duration} free`
    : ''

const personalLine = [benefitText, props.forClients].filter(Boolean).join(' ')
</script>

<template>
  <MembersInfoDialog v-model:is-dialog-visible="isMembersDialogOpen" />
  <RestaurantsInfoDialog v-model:is-dialog-visible="isRestaurantsDialogOpen" />

  <!--
    One hero for the home page AND all referral/activation pages — a single
    restrained serif sentence (no separate headline/subhead) → (join-detail
    line on referral pages) → single CTA → For Members | For Restaurants.
    The restaurant browser renders below this component on every page.
    The sentence is CMS richtext: each "&" is a quiet inline span (.amp),
    bound to the word after it (.nb) so an "&" never strands at a line end.
  -->
  <div class="hero">
    <CmsText
      k="home.masthead.headline"
      tag="h1"
      class="hero-headline"
    />

    <!--
      Personalized group line — one clean line between the sentence
      and the CTA on referral/activation pages 
    -->
    <p
      v-if="!homePage && personalLine"
      class="hero-personal-line"
      v-text="personalLine"
    />

    <!--
      CTA row: button with the audience links stacked quietly to its right.
      The links expand their panels in place on every page that shows the
      hero (home and all referral/activation pages) — no navigation.
    -->
    <div class="hero-cta-row">
      <VBtn
        v-if="(!myUser || myUser.userLevel == 'dummy') && (homePage || joinPage)"
        variant="flat"
        color="primary-dark"
        @click="loginDialogMode = 'activate'"
      >
        <span v-text="activateText" />
      </VBtn>
      <div class="hero-aud">
        <span
          class="hero-link"
          :class="{ 'hero-link--active': expandedSection === 'members' }"
          role="button"
          tabindex="0"
          @click="toggleSection('members')"
          @keydown.enter="toggleSection('members')"
        >
          <CmsText k="home.masthead.membersLink" />
        </span>
        <span
          class="hero-link"
          :class="{ 'hero-link--active': expandedSection === 'restaurants' }"
          role="button"
          tabindex="0"
          @click="toggleSection('restaurants')"
          @keydown.enter="toggleSection('restaurants')"
        >
          <CmsText k="home.masthead.restaurantsLink" />
        </span>
      </div>
    </div>

    <VExpandTransition>
      <div
        v-if="expandedSection"
        ref="expandedPanelEl"
        class="hero-info-panel"
      >
        <MembersInfoContent v-if="expandedSection === 'members'" />
        <PreferredTabsContent
          v-else
          show-request-account-button
          :show-preferred-list="false"
        />
      </div>
    </VExpandTransition>

    <div
      v-if="iosAppAvailable"
      class="mt-4 text-sm"
      style="color: #656565"
    >
      <CmsText k="home.masthead.iosNote" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.search-bar {
  max-width: 350px;
  margin: auto;
}

.v-text-field .v-field--active input {
  opacity: 1;
}

input[type="text"] {
  background: rgba(0, 0, 0, 0);
  border: none;
  outline: none;
}

.verified-icon {
  color: rgb(0, 149, 246);
  width: 1.25rem;
  height: 1.25rem;
  margin-bottom: 4px;
  margin-right: 1px;
}

.masthead-content {
  padding: 0 1rem;
  margin: 1rem 0;
}

/* Home page hero — text-only, left-aligned; compact enough that the CTA
   row and Members' Favorites stay on the first screen. Shares one centered
   720px content column with the Members' Favorites header + card list so
   every section has the same left edge (page wrappers add the side gutter
   on narrow screens). */
.hero {
  max-width: 720px;
  margin-inline: auto;
  padding: var(--ds-space-6) 0 var(--ds-space-5);
  text-align: left;
}

@media (min-width: 960px) {
  .hero {
    // Desktop: the shared ~1160px container's content width (1096px =
    // 1160 − 2×32 gutters), matching the favorites header + grid + navbar
    // contents so every section has the same left edge
    max-width: 1096px;
    padding: var(--ds-space-10) 0 var(--ds-space-8);
  }
}

/* One restrained serif sentence — small and light (Playfair 500, never
   bold), ~4 lines on mobile, so it reads as an elegant statement */
.hero-headline {
  font-family: var(--ds-font-heading);
  font-weight: var(--ds-font-medium);
  font-size: 1.25rem; // 20px mobile — holds the reference's ~4-line block
  line-height: var(--ds-leading-snug);
  letter-spacing: var(--ds-tracking-normal);
  color: var(--ds-brand-ink);
  margin: 0;

  // Even out the lines so the last word never strands alone ("hosting");
  // degrades gracefully where unsupported
  text-wrap: balance;
}

/* Tablet and up: a wide measure (~800px) so the sentence lands on TWO
   balanced lines at desktop size — a narrower cap stair-steps into an
   uneven 3-line block with a short first line */
@media (min-width: 600px) {
  .hero-headline {
    max-width: 880px;
  }
}

@media (min-width: 960px) {
  .hero-headline {
    font-size: var(--ds-text-3xl);
  }
}

/* The sentence arrives as CMS richtext (innerHTML), so the spans need
   :deep(). Each "&" is quieted — plain body face, smaller, soft ink —
   and .nb binds it to the following word so it can't end a line. */
.hero-headline :deep(.nb) {
  white-space: nowrap;
}

.hero-headline :deep(.amp) {
  font-family: var(--ds-font-body);
  font-weight: var(--ds-font-normal);
  font-size: 0.72em;
  color: color-mix(in srgb, var(--ds-brand-ink) 72%, var(--ds-surface-card));
  vertical-align: 0.04em;
  padding: 0 0.02em;
}

.v-theme--dark .hero-headline {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
}

.v-theme--dark .hero-headline :deep(.amp) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

/* Button + stacked audience links to its right (no pipe) */
.hero-cta-row {
  margin-top: var(--ds-space-5);
  display: flex;
  align-items: center;
  gap: var(--ds-space-4);
}

/* Two-tier shape system: pills are reserved for filter chips + city
   selector; buttons share the card corner radius (12px) */
.hero-cta-row :deep(.v-btn) {
  border-radius: 12px;
}

.hero-aud {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: var(--ds-text-base); // 14px — readable beside the CTA, per the prototype (14.5px)
  font-weight: var(--ds-font-medium);
  line-height: 1.3;
}

.hero-link {
  color: var(--ds-brand-primary-dark);
  text-decoration: none;
  cursor: pointer;
}

.hero-link:hover {
  text-decoration: underline;
}

.hero-link--active {
  text-decoration: underline;
}

/* One scrolling document: the panel renders at full natural height and
   pushes the discovery list down — no inner scroll region */
.hero-info-panel {
  max-width: 640px;
  margin: var(--ds-space-5) auto 0;
  text-align: left;
  border: 1px solid var(--ds-border-light);
  border-radius: 12px;
  background: var(--ds-surface-card);
  scroll-margin-top: 90px; // keep the panel top clear of the sticky nav on open
}

/* Referral / activation pages — single join-detail line between the
   sentence and the CTA; quiet, on-brand green */
.hero-personal-line {
  max-width: 56ch;
  margin: var(--ds-space-4) 0 0;
  font-size: var(--ds-text-sm);
  line-height: var(--ds-leading-normal);
  font-weight: var(--ds-font-medium);
  color: var(--ds-brand-primary-dark);
}
.subtitle {
  text-decoration: none;
}
.button-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.request-join-button {
  min-width: auto;
}

/* Match exact button font size */
.button-container :deep(.v-btn) {
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.025rem;
}

.how-rm-works-link {
  text-decoration: none !important;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.025rem;
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
  line-height: 1.5;
  white-space: nowrap;
}

.how-rm-works-link:hover {
  text-decoration: none !important;
}

.info-link {
  cursor: pointer;
}

.info-link:hover {
  text-decoration: underline;
}
</style>
